import { useNavigate } from "react-router-dom";
import { useGetMyBookingsQuery } from "../../bookings/bookingsApiSlice";
import Loading from "../../../shared/Loading";
import { MapPinIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import { BookingObj, TourObj } from "../../../shared/types";

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const BookingRow = ({
  booking,
  tour,
}: {
  booking: BookingObj;
  tour?: TourObj;
}) => {
  const navigate = useNavigate();
  if (!tour) return null;

  const upcoming = new Date(booking.startDate).getTime() >= Date.now();
  const status = upcoming ? "Confirmed" : "Completed";

  return (
    <div className="flex items-center gap-[18px] rounded-2xl border border-line-100 p-3.5">
      <img
        src={`${process.env.REACT_APP_API_URL}/img/tours/${tour.imageCover}`}
        alt={tour.name}
        crossOrigin="anonymous"
        className="h-[84px] w-[104px] flex-none rounded-xl object-cover"
      />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2.5">
          <span className="font-serif text-[19px] text-ink">{tour.name}</span>
          <span
            className={`rounded-full px-2.5 py-[3px] text-[11px] font-bold uppercase ${
              upcoming ? "bg-easy/10 text-easy" : "bg-[#ece7da] text-muted-400"
            }`}
          >
            {status}
          </span>
        </div>
        <div className="flex flex-wrap gap-4 text-[13px] text-muted-400">
          {tour.startLocation?.address && (
            <span className="flex items-center gap-1">
              <MapPinIcon className="h-4 w-4" /> {tour.startLocation.address}
            </span>
          )}
          <span className="flex items-center gap-1">
            <CalendarDaysIcon className="h-4 w-4" /> {fmtDate(booking.startDate)}
          </span>
        </div>
      </div>
      <div className="flex-none text-right">
        <div className="mb-2 text-[17px] font-extrabold text-ink">
          ${booking.price.toLocaleString()}
        </div>
        <button
          onClick={() => navigate(`/tours/${tour.id}`)}
          className="text-[13px] font-semibold text-secondary-400 transition-colors hover:text-ink"
        >
          {upcoming ? "View details →" : "Book again →"}
        </button>
      </div>
    </div>
  );
};

/** My Bookings panel — real getMyBookings fetch; status derived from the trip's start date. */
const UserBookings = () => {
  const { data, isLoading, isSuccess } = useGetMyBookingsQuery();

  const bookings = isSuccess ? data.data.data.bookings : [];
  const tours = isSuccess ? data.data.data.tours : [];

  return (
    <div className="rounded-[20px] bg-white p-[30px] shadow-[0_14px_30px_-22px_rgba(0,0,0,0.25)]">
      <h2 className="mb-1 font-serif text-[26px] font-normal text-ink">
        My bookings
      </h2>
      <p className="mb-6 text-[14px] text-muted-300">
        Your upcoming and past expeditions.
      </p>

      {isLoading ? (
        <Loading />
      ) : bookings.length ? (
        <div className="flex flex-col gap-4">
          {bookings.map((booking: BookingObj) => (
            <BookingRow
              key={booking._id}
              booking={booking}
              tour={tours.find((t: TourObj) => t.id === booking.tour.id)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-line px-5 py-12 text-center">
          <p className="text-[15px] text-muted">
            You haven't booked any expeditions yet.
          </p>
          <a
            href="/tours"
            className="mt-3 inline-block text-[14px] font-semibold text-secondary-400 hover:text-ink"
          >
            Browse tours →
          </a>
        </div>
      )}
    </div>
  );
};

export default UserBookings;
